<script setup lang="ts">
/**
 * Performer name cell for the Stuff table: row number beside the name, then
 * the period stat summary (business services count, total income, unique
 * clients) below — no roles line (dropped per feedback; roles still export
 * to CSV, just aren't shown in this cell).
 *
 * The row number lives here rather than in a separate DynamicTable `#`
 * column: a plain table column centers its content against the *whole
 * row's* height, which for this table is driven by the business-services
 * breakdown in the next column (often dozens of lines) — so the number
 * ended up floating in empty space instead of next to the name. Rendering
 * it inline with the name in one flex row means it centers against just
 * the name's own line height, which is what "center by height" actually
 * meant here.
 *
 * The summary stacks one stat per row (label left, value right) instead of
 * wrapping side by side — a fixed-width column can't reliably fit 3 stats
 * per row, and wrapping mid-set looked like the values were placed at
 * random. One row each is deterministic regardless of column width.
 */
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

defineProps<{
  /** 1-based row index (stable across the loaded list). */
  rowNumber: number
  /** Performer display name. */
  name: string
  /** Count of business services in the period (`stat.business_services_cnt`). */
  businessServicesCnt: number
  /** Pre-formatted total income for the period, e.g. "$1,907.00". */
  totalIncomeFormatted: string
  /** Count of distinct clients served in the period (`stat.unique_clients_cnt`). */
  uniqueClientsCnt: number
  /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
  maxWidth?: string
}>()
</script>

<template>
  <td
    class="performer-name-cell text-tufte-text font-tufte"
    :style="maxWidth ? { maxWidth: maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <div class="performer-name-cell__header">
      <span class="performer-name-cell__index">{{ rowNumber }}</span>
      <span class="performer-name-cell__name">{{ name }}</span>
    </div>
    <div class="performer-name-cell__summary">
      <div class="performer-name-cell__stat-row">
        <span class="performer-name-cell__lbl">{{ t('common.lessons') }}</span>
        <span class="performer-name-cell__val">{{ businessServicesCnt }}</span>
      </div>
      <div class="performer-name-cell__stat-row">
        <span class="performer-name-cell__lbl">{{ t('common.income') }}</span>
        <span class="performer-name-cell__val performer-name-cell__val--income">{{
          totalIncomeFormatted
        }}</span>
      </div>
      <div class="performer-name-cell__stat-row">
        <span class="performer-name-cell__lbl">{{ t('common.uniqueClients') }}</span>
        <span class="performer-name-cell__val">{{ uniqueClientsCnt }}</span>
      </div>
    </div>
  </td>
</template>

<style scoped>
/*
 * Vertically centered against the row's full height (DynamicTable's default
 * `vertical-align: middle`, left implicit here) — the row's height is driven
 * by the business-services breakdown in the next column, which can run to
 * dozens of lines, so this compact name+stats block sits centered in the
 * middle of that height rather than pinned to the top.
 */
.performer-name-cell {
  font-size: var(--text-tufte-lg);
}

.performer-name-cell__header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
}

/* Same muted, tabular-nums style as RowIndexCell, the standalone-column version this replaces. Smaller than the name text so it reads as secondary metadata, not a second name. */
.performer-name-cell__index {
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.performer-name-cell__summary {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-family: var(--font-tufte-sans);
}

.performer-name-cell__stat-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.performer-name-cell__lbl {
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

.performer-name-cell__val {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-tufte-text);
}

.performer-name-cell__val--income {
  color: var(--color-money-positive);
}
</style>
